import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import api, { API, errMsg } from "@/lib/api";import { useLang } from "@/lib/i18n";
import GuestsTab from "./GuestsTab";
import { BookingDetailDialog } from "./CalendarTab";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  ScanLine,
  Loader2,
  Pencil,
  Clock,
  Info,
  User,
  Coffee,
} from "lucide-react";

import { todayIso, otaBadgeColor, otaBadgeLabel } from "./reception/constants";
import StatusPill from "./reception/StatusPill";
import ContactActions from "./reception/ContactActions";
import OtaBookingDetailModal from "./bookings/OtaBookingDetailModal";
import ScanDialog from "./reception/ScanDialog";
import MealPrefsCell from "./reception/MealPrefsCell";

// Shared shape expected by OtaBookingDetailModal (label + brand color).
// Mirrors the helper in BookingsTab.jsx so the two entry points open the
// exact same editable modal for OTA rows.
const otaProviderLabel = (src) => {
  const s = (src || "").toLowerCase();
  if (s.includes("booking")) return { label: "Booking.com", color: "#003580" };
  if (s.includes("airbnb")) return { label: "Airbnb", color: "#FF5A5F" };
  if (s.includes("expedia")) return { label: "Expedia", color: "#F7B801" };
  if (s.includes("vrbo")) return { label: "Vrbo", color: "#2E86DE" };
  return { label: src || "OTA", color: "#666666" };
};

// -----------------------------------------------------------------------------
// ArrivalTimeCell — inline "HH:MM" editor used in the ETA column.
//
// Fallback order for the read state:
//   1) guest_eta        — value the guest submitted via the Portal (source of
//                         truth, sage badge with clock icon)
//   2) arrival_time     — value the host declared here or at booking time
//                         (muted badge with pencil icon, still editable)
//   3) "—"              — nothing on file yet
//
// Persistence: PATCH /bookings/{id}/arrival-time — the same endpoint that
// backs the "Modifica prenotazione" dialog. `guest_eta` is never touched by
// this input (it's a distinct field that only the guest can write).
// Module-level so the parent re-render (SSE, date change) doesn't blur the
// input mid-edit.
// -----------------------------------------------------------------------------
function ArrivalTimeCell({ booking, IT, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(booking.arrival_time || "");
  const [saving, setSaving] = useState(false);
  // Keep local state in sync when the row is refreshed (parent re-fetch,
  // SSE update, etc.). Runs while the input is closed so we don't stomp on
  // the host mid-type.
  useEffect(() => {
    if (!editing) setVal(booking.arrival_time || "");
  }, [booking.arrival_time, editing]);

  const save = async (next) => {
    const clean = (next || "").trim();
    // No-op if the value didn't change — avoids a redundant network hop.
    if (clean === (booking.arrival_time || "")) { setEditing(false); return; }
    setSaving(true);
    try {
      await api.patch(`/bookings/${booking.id}/arrival-time`, { arrival_time: clean });
      onSaved?.(booking.id, clean || null);
      toast.success(IT ? "Orario aggiornato" : "Arrival time updated");
      setEditing(false);
    } catch (e) {
      toast.error(errMsg(e, IT ? "Errore" : "Error"));
      // Roll back the local state to whatever the parent still has.
      setVal(booking.arrival_time || "");
    } finally { setSaving(false); }
  };

  // Confirmed-by-guest badge takes precedence over anything else.
  if (booking.guest_eta && !editing) {
    return (
      <button
        type="button"
        onClick={() => setEditing(true)}
        className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-sage/15 text-sage border border-sage/30 font-mono font-semibold hover:bg-sage/25 transition-colors"
        title={booking.guest_eta_at
          ? (IT ? `Comunicato dall'ospite il ${new Date(booking.guest_eta_at).toLocaleString('it-IT')} · clicca per sovrascrivere` : `Sent by the guest on ${new Date(booking.guest_eta_at).toLocaleString('en-US')} · click to override`)
          : (IT ? "Comunicato dall'ospite · clicca per sovrascrivere" : "Sent by the guest · click to override")}
        data-testid={`arrival-eta-${booking.id}`}
      >
        <Clock className="w-3 h-3"/>
        {booking.guest_eta}
      </button>
    );
  }

  if (editing) {
    return (
      <div className="inline-flex items-center gap-1" data-testid={`arrival-time-edit-${booking.id}`}>
        <input
          type="time"
          value={val}
          disabled={saving}
          autoFocus
          onChange={(e) => setVal(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") save(val);
            if (e.key === "Escape") { setVal(booking.arrival_time || ""); setEditing(false); }
          }}
          onBlur={() => save(val)}
          className="h-7 px-1.5 rounded border border-primary/40 bg-background text-xs font-mono focus:outline-none focus:ring-1 focus:ring-primary/50"
          data-testid={`arrival-time-input-${booking.id}`}
        />
      </div>
    );
  }

  // Host-declared value (or nothing yet) — always clickable to edit.
  return (
    <button
      type="button"
      onClick={() => setEditing(true)}
      className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full border transition-colors ${booking.arrival_time
        ? "bg-primary/10 text-primary border-primary/30 font-mono font-semibold hover:bg-primary/20"
        : "bg-card text-muted-foreground border-dashed border-border hover:border-primary/40 hover:text-primary italic"}`}
      title={booking.arrival_time
        ? (IT ? "Orario dichiarato dall'host · clicca per modificare" : "Host-declared time · click to edit")
        : (IT ? "Nessun orario · clicca per aggiungere" : "No arrival time · click to add")}
      data-testid={booking.arrival_time ? `arrival-time-host-${booking.id}` : `arrival-eta-pending-${booking.id}`}
    >
      {booking.arrival_time ? <Pencil className="w-3 h-3"/> : <Clock className="w-3 h-3 opacity-60"/>}
      {booking.arrival_time || (IT ? "Aggiungi" : "Add")}
    </button>
  );
}

// -----------------------------------------------------------------------------
// Reception Dashboard tab: today's arrivals + OCR-assisted check-in
// Modularised in Iteration 62 (2026-08-31) — presentational children live in
// ./reception/*, this file keeps the arrivals/departures list, deep-linking,
// SSE live-ETA sync, and dialog orchestration.
// -----------------------------------------------------------------------------

export default function ReceptionTab() {
  const { lang } = useLang();
  const IT = lang === "it";
  const [subview, setSubview] = useState("arrivals"); // "arrivals" | "registrations"
  const [date, setDate] = useState(todayIso());
  const [arrivals, setArrivals] = useState([]);
  const [loading, setLoading] = useState(false);
  const [scanFor, setScanFor] = useState(null); // booking (OCR scan mode)
  const [manualFor, setManualFor] = useState(null); // booking (manual entry mode — skips OCR)
  // Booking-edit dialog — mirrors the "Modifica" button in the Prenotazioni tab
  // so a receptionist can fix name/dates/notes without leaving Reception.
  const [openedBookingId, setOpenedBookingId] = useState(null);
  // OTA read-only detail — OTA arrivals cannot be edited from here (source of
  // truth is Booking.com/Airbnb Extranet), so we surface the same read-only
  // modal used in BookingsTab.
  const [otaDetail, setOtaDetail] = useState(null);
  // Day view: "arrivals" = who checks in on `date`, "departures" = who checks out.
  // Split so the same Reception UI can drive both "chi entra oggi" and "chi
  // esce oggi" workflows without leaving the tab.
  const [dayView, setDayView] = useState("arrivals");
  const [rooms, setRooms] = useState([]);
  // House-rules check-in time (e.g. "15:00") — used to pre-compose the WhatsApp
  // greeting. Pulled from the single property we manage; falls back to empty.
  const [checkinTime, setCheckinTime] = useState("");
  // Deep-link: Web Push notifications for guest_portal_opened / guest_eta_confirmed
  // land here as ?tab=reception&booking=<id>. Highlight the matching row so the
  // host immediately sees which guest triggered the alert.
  const [searchParams, setSearchParams] = useSearchParams();
  const deepBookingId = searchParams.get("booking") || "";
  const [highlightId, setHighlightId] = useState("");
  // Property-level toggle for "orario di arrivo obbligatorio".
  const [property, setProperty] = useState(null);
  const [savingReqArrival, setSavingReqArrival] = useState(false);
  // Daily breakfast counter — sums `guests` on in-house bookings with
  // ``breakfast=true`` for the selected date. Powers the "N colazioni oggi"
  // top-bar card so the cook knows how many covers to prep.
  const [breakfastStats, setBreakfastStats] = useState({ bookings: 0, covers: 0 });
  useEffect(() => {
    api.get("/rooms/me").then((r) => setRooms(r.data || [])).catch(() => setRooms([]));
    api.get("/properties/me")
      .then((r) => {
        setProperty(r.data || null);
        setCheckinTime(r.data?.house_rules?.checkin || "");
      })
      .catch(() => setCheckinTime(""));
  }, []);

  // Refetch the in-house list whenever the selected date changes so the
  // breakfast counter reflects who slept at the property the night before
  // ``date`` (i.e. the guests eating breakfast the morning of ``date``).
  useEffect(() => {
    let alive = true;
    api.get(`/reception/in-house?date=${date}`)
      .then((r) => {
        if (!alive) return;
        const rows = r.data?.in_house || [];
        let bookings = 0, covers = 0;
        for (const b of rows) {
          if (!b.breakfast) continue;
          bookings++;
          covers += Math.max(1, Number(b.guests) || 1);
        }
        setBreakfastStats({ bookings, covers });
      })
      .catch(() => { if (alive) setBreakfastStats({ bookings: 0, covers: 0 }); });
    return () => { alive = false; };
    // Re-run when the host toggles breakfast/dietary on an arrival row too.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date, arrivals]);
  const toggleRequireArrivalTime = async (next) => {
    if (savingReqArrival) return;
    setSavingReqArrival(true);
    try {
      const r = await api.put("/properties/me", { require_arrival_time: next });
      setProperty(r.data || property);
      toast.success(next
        ? (IT ? "Orario di arrivo ora obbligatorio" : "Arrival time now required")
        : (IT ? "Orario di arrivo ora opzionale" : "Arrival time now optional"));
    } catch (e) {
      toast.error(errMsg(e, IT ? "Errore" : "Error"));
    } finally { setSavingReqArrival(false); }
  };

  const load = async () => {
    if (subview !== "arrivals") return;
    setLoading(true);
    try {
      // In-house shares the arrivals row shape (reception_status arrived|registered|pending),
      // departures uses departed|in_house|pending. The three endpoints share the same
      // downstream rendering so the table can loop through a single array.
      const url = dayView === "departures" ? "/reception/departures"
                : dayView === "in_house"   ? "/reception/in-house"
                : "/reception/arrivals";
      const key = dayView === "departures" ? "departures"
                : dayView === "in_house"   ? "in_house"
                : "arrivals";
      const r = await api.get(url, { params: { date } });
      setArrivals(r.data[key] || []);
    } catch (e) {
      toast.error(errMsg(e, IT ? "Errore" : "Error"));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date, subview, dayView]);

  // Live ETA — subscribe to the notifications SSE and patch matching arrival
  // rows the moment the guest submits their arrival time on the Guest Portal
  // (works for both direct bookings and OTA magic-link sessions). Also patches
  // `portal_opened_at` when the guest first opens the portal so hosts see the
  // "clicked" signal without refreshing.
  useEffect(() => {
    if (subview !== "arrivals") return;
    const token = localStorage.getItem("hh_token");
    if (!token) return;
    const es = new EventSource(`${API}/notifications/stream?token=${encodeURIComponent(token)}`);
    es.onmessage = (ev) => {
      try {
        const n = JSON.parse(ev.data);
        const bid = n.booking_id;
        if (!bid) return;
        // Reception rows use the RAW ical_event id (no `ota-` prefix), while
        // notify() uses the deep-link id `ota-<event>`. Normalise both.
        const rawId = bid.startsWith("ota-") ? bid.slice(4) : bid;
        if (n.kind === "guest_eta_confirmed" && n.eta) {
          setArrivals((prev) => prev.map((a) =>
            (a.id === bid || a.id === rawId)
              ? { ...a, guest_eta: n.eta, guest_eta_at: n.eta_at || new Date().toISOString() }
              : a
          ));
          setHighlightId(rawId);
          setTimeout(() => setHighlightId((h) => h === rawId ? "" : h), 4000);
          const first = (arrivals.find((a) => a.id === bid || a.id === rawId)?.guest_name || "").split(" ")[0] || (IT ? "Ospite" : "Guest");
          toast.success(IT ? `${first} arriva verso le ${n.eta}` : `${first} arriving around ${n.eta}`);
        } else if (n.kind === "guest_portal_opened") {
          setArrivals((prev) => prev.map((a) =>
            (a.id === bid || a.id === rawId)
              ? { ...a, portal_opened_at: new Date().toISOString() }
              : a
          ));
        }
      } catch { /* ignore malformed SSE messages */ }
    };
    es.onerror = () => { /* browser auto-reconnects */ };
    return () => es.close();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subview]);

  // When a booking id arrives via ?booking=<id>, snap the date picker to that
  // booking's check-in day and highlight the row for ~4s so the host sees it
  // immediately. Handles direct + OTA (`ota-` / `cb-` / raw ical) ids via the
  // unified `/reception/resolve/{id}` endpoint so a deep-link from a Web Push
  // notification always lands on the right day.
  useEffect(() => {
    if (!deepBookingId) return;
    setSubview("arrivals");
    const rawFromDeep = deepBookingId.startsWith("ota-") ? deepBookingId.slice(4)
                        : deepBookingId.startsWith("cb-") ? deepBookingId.slice(3)
                        : deepBookingId;
    const match = arrivals.find((a) => a.id === deepBookingId || a.id === rawFromDeep);
    if (match) {
      setHighlightId(match.id);
      const t = setTimeout(() => setHighlightId(""), 4000);
      const next = new URLSearchParams(searchParams);
      next.delete("booking");
      setSearchParams(next, { replace: true });
      return () => clearTimeout(t);
    }
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get(`/reception/resolve/${encodeURIComponent(deepBookingId)}`);
        const ci = (r.data?.check_in || "").slice(0, 10);
        if (ci && !cancelled) {
          if (ci === date) {
            load();
          } else {
            setDate(ci);
          }
          setHighlightId(r.data?.raw_id || r.data?.id || deepBookingId);
          setTimeout(() => { if (!cancelled) setHighlightId(""); }, 4500);
        }
      } catch { /* booking not accessible / not found — leave date as-is */ }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deepBookingId, arrivals.length]);

  // Toggle "Arrivato" — flips checked_in_at on the booking (or ical_event)
  // and re-fetches the list so the header stats + row pill update in one
  // shot. Called from the row-level checkbox in the "Arrivato" column.
  const toggleArrived = async (b, next) => {
    try {
      const isOta = !!b.is_ota || (b.source && String(b.source).toLowerCase() !== "direct");
      const url = dayView === "departures" ? "/reception/mark-departed" : "/reception/mark-arrived";
      const payload = dayView === "departures"
        ? { booking_id: b.id, is_ota: isOta, departed: next }
        : { booking_id: b.id, is_ota: isOta, arrived: next };
      await api.post(url, payload);
      setArrivals((prev) => prev.map((a) => {
        if (a.id !== b.id) return a;
        if (dayView === "departures") {
          return { ...a, checked_out_at: next ? new Date().toISOString() : null,
                   reception_status: next ? "departed" : (a.checked_in_at ? "in_house" : "pending") };
        }
        return { ...a, checked_in_at: next ? new Date().toISOString() : null,
                 reception_status: next ? "arrived" : (a.registrations_count > 0 ? "registered" : "pending") };
      }));
      load();
    } catch (e) {
      toast.error(errMsg(e, IT ? "Errore" : "Error"));
    }
  };

  const shiftDay = (delta) => {
    const d = new Date(date);
    d.setDate(d.getDate() + delta);
    setDate(d.toISOString().slice(0, 10));
  };

  const stats = useMemo(() => {
    if (dayView === "departures") {
      const departed = arrivals.filter((a) => a.reception_status === "departed").length;
      const in_house = arrivals.filter((a) => a.reception_status === "in_house").length;
      const pending = arrivals.filter((a) => a.reception_status === "pending").length;
      return { departed, in_house, pending, total: arrivals.length };
    }
    const arrived = arrivals.filter((a) => a.reception_status === "arrived").length;
    const registered = arrivals.filter((a) => a.reception_status === "registered").length;
    const pending = arrivals.filter((a) => a.reception_status === "pending").length;
    return { arrived, registered, pending, total: arrivals.length };
  }, [arrivals, dayView]);

  return (
    <div className="space-y-4">
      {/* Sub-view switcher: Arrivi oggi (workflow) vs Registrazioni (registro storico + file Questura) */}
      <div className="flex items-center gap-1 border-b border-border" data-testid="reception-subnav">
        <button
          type="button"
          onClick={() => setSubview("arrivals")}
          className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
            subview === "arrivals"
              ? "border-primary text-primary"
              : "border-transparent text-muted-foreground hover:text-foreground"
          }`}
          data-testid="reception-sub-arrivals"
        >
          {IT ? "Arrivi & scansione" : "Arrivals & scan"}
        </button>
        <button
          type="button"
          onClick={() => setSubview("registrations")}
          className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
            subview === "registrations"
              ? "border-primary text-primary"
              : "border-transparent text-muted-foreground hover:text-foreground"
          }`}
          data-testid="reception-sub-registrations"
        >
          {IT ? "Registrazioni · Questura" : "Registry · Police"}
        </button>
      </div>

      {subview === "registrations" ? (
        <GuestsTab/>
      ) : (
      <>
      {/* Header — date picker + counters */}
      <div className="flex flex-wrap items-end gap-3 justify-between">
        <div>
          <div className="uppercase tracking-widest text-[10px] text-sage font-semibold mb-1">
            {IT ? "Reception" : "Reception"}
          </div>
          <div className="font-display text-2xl">
            {dayView === "departures"
              ? (IT ? "Partenze del giorno" : "Today's departures")
              : dayView === "in_house"
                ? (IT ? "Soggiorni in corso" : "Stays in progress")
                : (IT ? "Arrivi del giorno" : "Today's arrivals")}
          </div>
          {/* Day-view segmented toggle: Chi entra oggi ↔ Soggiorni in corso ↔ Chi esce oggi. */}
          <div className="mt-2 inline-flex rounded-full border border-border overflow-hidden text-xs" data-testid="reception-dayview-toggle">
            <button
              type="button"
              onClick={() => setDayView("arrivals")}
              className={`px-3 py-1.5 transition-all ${dayView === "arrivals" ? "bg-sage text-white" : "bg-card text-muted-foreground hover:text-foreground"}`}
              data-testid="reception-dayview-arrivals"
            >
              {IT ? "Chi entra oggi" : "Arriving today"}
            </button>
            <button
              type="button"
              onClick={() => setDayView("in_house")}
              className={`px-3 py-1.5 transition-all border-l border-border ${dayView === "in_house" ? "bg-primary/80 text-primary-foreground" : "bg-card text-muted-foreground hover:text-foreground"}`}
              data-testid="reception-dayview-in-house"
            >
              {IT ? "Soggiorni in corso" : "Stays in progress"}
            </button>
            <button
              type="button"
              onClick={() => setDayView("departures")}
              className={`px-3 py-1.5 transition-all border-l border-border ${dayView === "departures" ? "bg-primary text-primary-foreground" : "bg-card text-muted-foreground hover:text-foreground"}`}
              data-testid="reception-dayview-departures"
            >
              {IT ? "Chi esce oggi" : "Departing today"}
            </button>
          </div>
          {/* Property-level toggle — obbligatorietà dell'orario di arrivo
              nel form di prenotazione pubblico (Vetrina). Quando è ON, il
              guest DEVE compilarlo al momento del booking; quando è OFF,
              resta un campo opzionale che l'host può ancora chiedere
              via WhatsApp/Guest Portal in seguito. */}
          <div className="mt-3 inline-flex items-center gap-3 rounded-md border border-border bg-card px-3 py-2" data-testid="reception-require-arrival-row">
            <Switch
              checked={!!property?.require_arrival_time}
              disabled={savingReqArrival || !property}
              onCheckedChange={(next) => toggleRequireArrivalTime(next)}
              data-testid="reception-require-arrival-toggle"
              aria-label={IT ? "Orario di arrivo obbligatorio alla prenotazione" : "Arrival time required at booking"}
            />
            <div className="flex flex-col leading-tight">
              <span className="text-xs font-medium text-foreground">
                {IT ? "Orario di arrivo obbligatorio alla prenotazione" : "Arrival time required at booking"}
              </span>
              <span className="text-[10px] text-muted-foreground">
                {property?.require_arrival_time
                  ? (IT ? "Il guest deve indicarlo nel form di prenotazione." : "Guests must declare it in the booking form.")
                  : (IT ? "Campo opzionale nel form di prenotazione." : "Optional field in the booking form.")}
              </span>
            </div>
          </div>
        </div>
        <div className="flex items-end gap-2 flex-wrap">
          <Button variant="outline" size="sm" onClick={() => shiftDay(-1)} data-testid="reception-prev-day">←</Button>
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">
              {IT ? "Data" : "Date"}
            </Label>
            <Input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="h-9 w-40"
              data-testid="reception-date-input"
            />
          </div>
          <Button variant="outline" size="sm" onClick={() => shiftDay(1)} data-testid="reception-next-day">→</Button>
          <Button variant="outline" size="sm" onClick={() => setDate(todayIso())} data-testid="reception-today">
            {IT ? "Oggi" : "Today"}
          </Button>
        </div>
      </div>

      {/* Counters — swap labels when the host is looking at departures. */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        <div className="border border-border rounded-md p-3 bg-card" data-testid="reception-stat-total">
          <div className="uppercase tracking-widest text-[9px] text-muted-foreground">{IT ? "Totale" : "Total"}</div>
          <div className="text-2xl font-display">{stats.total}</div>
        </div>
        <div
          className="border border-amber-500/30 rounded-md p-3 bg-amber-500/5"
          data-testid="reception-stat-breakfast"
          title={IT
            ? `${breakfastStats.bookings} prenotazioni con colazione · ${breakfastStats.covers} coperti totali`
            : `${breakfastStats.bookings} bookings with breakfast · ${breakfastStats.covers} total covers`}
        >
          <div className="uppercase tracking-widest text-[9px] text-amber-700 flex items-center gap-1">
            <Coffee className="w-3 h-3"/>
            {IT ? "Colazioni oggi" : "Breakfasts today"}
          </div>
          <div className="text-2xl font-display text-amber-700" data-testid="reception-stat-breakfast-covers">
            {breakfastStats.covers}
          </div>
          <div className="text-[10px] text-muted-foreground mt-0.5">
            {IT ? `${breakfastStats.bookings} prenotazioni` : `${breakfastStats.bookings} bookings`}
          </div>
        </div>
        {dayView === "departures" ? (
          <>
            <div className="border border-border rounded-md p-3 bg-amber-500/5" data-testid="reception-stat-dep-pending">
              <div className="uppercase tracking-widest text-[9px] text-amber-700">{IT ? "Attesi" : "Pending"}</div>
              <div className="text-2xl font-display text-amber-700">{stats.pending}</div>
            </div>
            <div className="border border-border rounded-md p-3 bg-primary/5" data-testid="reception-stat-in-house">
              <div className="uppercase tracking-widest text-[9px] text-primary">{IT ? "In casa" : "In house"}</div>
              <div className="text-2xl font-display text-primary">{stats.in_house}</div>
            </div>
            <div className="border border-border rounded-md p-3 bg-sage/5" data-testid="reception-stat-departed">
              <div className="uppercase tracking-widest text-[9px] text-sage">{IT ? "Partiti" : "Departed"}</div>
              <div className="text-2xl font-display text-sage">{stats.departed}</div>
            </div>
          </>
        ) : (
          <>
            <div className="border border-border rounded-md p-3 bg-amber-500/5" data-testid="reception-stat-pending">
              <div className="uppercase tracking-widest text-[9px] text-amber-700">{IT ? "Da registrare" : "Pending"}</div>
              <div className="text-2xl font-display text-amber-700">{stats.pending}</div>
            </div>
            <div className="border border-border rounded-md p-3 bg-primary/5" data-testid="reception-stat-registered">
              <div className="uppercase tracking-widest text-[9px] text-primary">{IT ? "Registrati" : "Registered"}</div>
              <div className="text-2xl font-display text-primary">{stats.registered}</div>
            </div>
            <div className="border border-border rounded-md p-3 bg-sage/5" data-testid="reception-stat-arrived">
              <div className="uppercase tracking-widest text-[9px] text-sage">{IT ? "Arrivati" : "Arrived"}</div>
              <div className="text-2xl font-display text-sage">{stats.arrived}</div>
            </div>
          </>
        )}
      </div>

      {/* Table */}
      <div className="overflow-x-auto border border-border rounded-md">
        <table className="w-full text-sm" data-testid="reception-arrivals-table">
          <thead className="bg-muted/50">
            <tr>
              <th className="text-left p-3">{IT ? "Ospite" : "Guest"}</th>
              <th className="text-left p-3">{IT ? "Contatta" : "Contact"}</th>
              <th className="text-left p-3">ETA</th>
              <th className="text-left p-3">{IT ? "Camera" : "Room"}</th>
              <th className="text-left p-3 whitespace-nowrap">{IT ? "Ospiti" : "Guests"}</th>
              <th className="text-left p-3">{IT ? "Notti" : "Nights"}</th>
              <th className="text-left p-3">{IT ? "Importo" : "Amount"}</th>
              <th className="text-left p-3 whitespace-nowrap" title={IT ? "Colazione + note alimentari (uso interno, non su Alloggiati)" : "Breakfast + dietary notes (internal only, never on Alloggiati)"}>
                {IT ? "Colazione" : "Meals"}
              </th>
              <th className="text-left p-3">{IT ? "Stato" : "Status"}</th>
              <th className="text-center p-3">{dayView === "departures" ? (IT ? "Partito" : "Departed") : (IT ? "Arrivato" : "Arrived")}</th>
              <th className="text-left p-3">{IT ? "Registrazioni" : "Registrations"}</th>
              <th className="text-left p-3">{IT ? "Azioni" : "Actions"}</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={10} className="p-8 text-center text-muted-foreground">
                <Loader2 className="w-5 h-5 animate-spin inline mr-2"/>
                {IT ? "Caricamento…" : "Loading…"}
              </td></tr>
            )}
            {!loading && arrivals.length === 0 && (
              <tr><td colSpan={10} className="p-8 text-center text-muted-foreground">
                {IT ? "Nessun arrivo previsto per questa data" : "No arrivals scheduled for this date"}
              </td></tr>
            )}
            {!loading && arrivals.map((b) => {
              const isOtaRow = !!b.is_ota || (b.source && String(b.source).toLowerCase() !== "direct" && b.payment_status === "ota");
              return (
              <tr key={b.id} className={`border-t border-border transition-colors ${highlightId === b.id ? "bg-amber-100/70 ring-2 ring-amber-400 ring-inset" : ""}`} data-testid={`arrival-row-${b.id}`}>
                <td className="p-3">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <button
                      type="button"
                      onClick={() => (isOtaRow ? setOtaDetail(b) : setOpenedBookingId(b.id))}
                      className="font-semibold text-primary hover:underline underline-offset-2 decoration-primary/40 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-primary rounded"
                      data-testid={`arrival-guest-name-${b.id}`}
                      title={IT ? "Apri scheda prenotazione (Modifica)" : "Open booking sheet (Edit)"}
                    >
                      {b.guest_name || "—"}
                    </button>
                    {isOtaRow && (
                      <span className="text-[9px] uppercase tracking-widest text-white font-semibold rounded px-1.5 py-0.5" style={{ background: otaBadgeColor(b.source) }} title={IT ? "Prenotazione importata da OTA" : "Booking imported from OTA"}>
                        {otaBadgeLabel(b.source)}
                      </span>
                    )}
                  </div>
                  {b.guest_email && <div className="text-xs text-muted-foreground">{b.guest_email}</div>}
                </td>
                <td className="p-3">
                  <ContactActions booking={b} IT={IT} checkinTime={checkinTime}/>
                </td>
                <td className="p-3">
                  <ArrivalTimeCell
                    booking={b}
                    IT={IT}
                    onSaved={(id, val) => setArrivals((prev) => prev.map((a) => a.id === id ? { ...a, arrival_time: val } : a))}
                  />
                </td>
                <td className="p-3">{b.room_name || "—"}</td>
                <td className="p-3 whitespace-nowrap" data-testid={`arrival-guests-${b.id}`}>
                  {b.guests != null && b.guests > 0 ? (
                    <span
                      className="inline-flex items-center gap-1 text-xs bg-muted text-foreground rounded-full px-2 py-0.5"
                      title={IT ? `${b.guests} ${b.guests === 1 ? "ospite" : "ospiti"}` : `${b.guests} ${b.guests === 1 ? "guest" : "guests"}`}
                    >
                      <User className="w-3 h-3 text-primary"/>
                      <span className="font-semibold">{b.guests}</span>
                    </span>
                  ) : (
                    <span className="text-xs text-muted-foreground">—</span>
                  )}
                </td>
                <td className="p-3">{b.nights ?? "—"}</td>
                <td className="p-3">{b.amount != null ? `€ ${Number(b.amount).toFixed(2)}` : "—"}</td>
                <td className="p-3" onClick={(e) => e.stopPropagation()}>
                  <MealPrefsCell
                    booking={b}
                    IT={IT}
                    onSaved={(id, patch) => setArrivals((prev) => prev.map((a) => a.id === id ? { ...a, ...patch } : a))}
                  />
                </td>
                <td className="p-3"><StatusPill status={b.reception_status} IT={IT}/></td>
                <td className="p-3 text-center">
                  <label className="inline-flex items-center justify-center cursor-pointer" title={dayView === "departures" ? (IT ? "Segna come partito" : "Mark as departed") : (IT ? "Segna come arrivato" : "Mark as arrived")}>
                    <input
                      type="checkbox"
                      checked={dayView === "departures" ? b.reception_status === "departed" : b.reception_status === "arrived"}
                      onChange={(e) => toggleArrived(b, e.target.checked)}
                      className="w-5 h-5 accent-sage cursor-pointer"
                      data-testid={`${dayView === "departures" ? "departure" : "arrival"}-checkbox-${b.id}`}
                      aria-label={dayView === "departures"
                        ? (IT ? `Segna ${b.guest_name} come partito` : `Mark ${b.guest_name} as departed`)
                        : (IT ? `Segna ${b.guest_name} come arrivato` : `Mark ${b.guest_name} as arrived`)}
                    />
                  </label>
                </td>
                <td className="p-3">{b.registrations_count || 0}</td>
                <td className="p-3">
                  <div className="flex flex-wrap items-center gap-2">
                    <Button
                      size="sm"
                      onClick={() => setScanFor(b)}
                      className="bg-primary text-primary-foreground hover:bg-primary/90 lift gap-1.5"
                      data-testid={`scan-btn-${b.id}`}
                      title={IT ? "Scansiona documento d'identità dell'ospite" : "Scan guest ID document"}
                    >
                      <ScanLine className="w-3.5 h-3.5"/>
                      {IT ? "Scansiona" : "Scan"}
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setManualFor(b)}
                      className="gap-1.5 border-primary/40 text-primary hover:bg-primary hover:text-primary-foreground"
                      data-testid={`manual-btn-${b.id}`}
                      title={IT ? "Inserisci i dati dell'ospite manualmente" : "Enter guest data manually"}
                    >
                      <Pencil className="w-3.5 h-3.5"/>
                      {IT ? "Manuale" : "Manual"}
                    </Button>
                    {isOtaRow ? (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setOtaDetail(b)}
                        className="gap-1.5"
                        title={IT ? "Vedi/modifica dettagli prenotazione OTA" : "View/edit OTA reservation details"}
                        data-testid={`arrival-ota-detail-btn-${b.id}`}
                      >
                        <Info className="w-3.5 h-3.5"/>
                        {IT ? "Dettagli OTA" : "OTA details"}
                      </Button>
                    ) : (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setOpenedBookingId(b.id)}
                        className="gap-1.5"
                        title={IT ? "Modifica prenotazione" : "Edit booking"}
                        data-testid={`arrival-edit-btn-${b.id}`}
                      >
                        <Pencil className="w-3.5 h-3.5"/>
                        {IT ? "Modifica" : "Edit"}
                      </Button>
                    )}
                  </div>
                </td>
              </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <ScanDialog
        booking={scanFor}
        property={property}
        onClose={() => setScanFor(null)}
        onDone={() => { setScanFor(null); load(); }}
        onArrivalTimeSaved={(id, val) => setArrivals((prev) => prev.map((a) => a.id === id ? { ...a, arrival_time: val } : a))}
      />
      <ScanDialog
        booking={manualFor}
        property={property}
        startInManual
        onClose={() => setManualFor(null)}
        onDone={() => { setManualFor(null); load(); }}
        onArrivalTimeSaved={(id, val) => setArrivals((prev) => prev.map((a) => a.id === id ? { ...a, arrival_time: val } : a))}
      />
      <BookingDetailDialog
        bookingId={openedBookingId}
        rooms={rooms}
        onClose={() => setOpenedBookingId(null)}
        onChanged={() => { setOpenedBookingId(null); load(); }}
      />
      <OtaBookingDetailModal
        item={otaDetail}
        onClose={() => setOtaDetail(null)}
        otaProviderLabel={otaProviderLabel}
        IT={IT}
        rooms={rooms || []}
        onSaved={() => load()}
      />
      </>
      )}
    </div>
  );
}
